import { useState, useEffect } from 'react';
import { useForm, Link } from '@inertiajs/react';
import { Table } from '../Components/table';
import { processZipFile } from '../Utils/dicom.js';
import Instance from '../Utils/instance.js';
import { getAllInstances } from '../Utils/indexedDB.js';
import clsx from 'clsx';
import axios from 'axios';

// --- Shared helpers ---
export const shortenUUID = (uuid) => uuid.split('-').slice(0, 2).join('');

const formatDuration = (duration) => {
  if (!duration) return '';
  const seconds = Math.floor(duration % 60);
  const minutes = Math.floor((duration / 60) % 60);
  const hours = Math.floor((duration / (60 * 60)) % 24);
  return `${hours}h ${minutes}m ${seconds}s`;
};

const formattedDate = (date) => {
  if (!date) return '';
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(date).toLocaleDateString(undefined, options);
};

const TaskStatusBadge = ({ status }) => {
  const task_status = {
    created: { label: 'Created', color: 'bg-gray-100 text-gray-800' },
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    in_progress: { label: 'Running', color: 'bg-blue-100 text-blue-800' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
    expired: { label: 'Expired', color: 'bg-red-100 text-red-800' },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800 line-through' },
  }

  return (
    <span className={clsx(
      "inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium cursor-default select-none", 
      task_status[status].color)
    }>
      { task_status[status].label }
    </span>
  )
};

const handleDownload = async (task) => {
  try {
    const response = await fetch(`/tasks/${task.id}/presigned-url`);
    if (!response.ok) throw new Error('Failed to fetch presigned URL');
    const data = await response.json();
    const link = document.createElement('a');
    link.href = data.presigned_url;
    link.download = `${task.SeriesInstanceUID}_${task.model.name}.zip`;
    link.click();
  } catch (error) {
    console.error('Error downloading task:', error);
  }
};

const handleDownloadIntoDicomStore = async ({ task, onProgress, onDownload, onDone } ) => {
  try {
    
    // get presigned URL via axios, json
    const { data: { presigned_url } } = await axios.get(`/tasks/${task.id}/presigned-url`);
    onProgress && onProgress(10);

    // download the file and process it, use axios and progress
    const response = await axios.get(presigned_url, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Download progress: ${percentCompleted}%`);
        onProgress && onProgress(10 + 0.5 * percentCompleted);
      },
    });

    // create a file from the response data
    const file = new File([response.data], `${task.SeriesInstanceUID}_${task.model.name}.zip`, { type: 'application/zip' });

    // download callback
    onDownload && onDownload({task, file});

    // process the zip file
    processZipFile(file, () => {}, (progress) => {
        console.log('Processing zip file progress:', progress*100);
        onProgress && onProgress(60 + 0.2 * progress*100);
    }, (instanceFile) => {
      console.log('File processed:', instanceFile);
      const dicomData = instanceFile.dicomData;

      // create instance object
      // TODO: outsource initialization of instance object into Instance singleton
      // TODO: this method now is build to handle a single file only, if there is multiple dicom files in the zip this will not work
      const instanceObj = {
        selected: true,
        PatientID: dicomData.PatientID,
        PatientName: dicomData.PatientName,
        Modality: dicomData.Modality,
        StudyDescription: dicomData.StudyDescription,
        StudyInstanceUID: dicomData.StudyInstanceUID,
        SeriesInstanceUID: dicomData.SeriesInstanceUID,
        SOPInstanceUID: dicomData.SOPInstanceUID,
        ReferencedSeriesInstanceUID: dicomData.ReferencedSeriesInstanceUID,
        Files: [instanceFile],                        // <---- only for one file
        local: false, // will become true later anyways, the store() method will set it
        size: instanceFile.fileData?.byteLength,      // <----- only for one file
        importDate: new Date().toISOString(), // Set import date
        taskId: task.id, // Associate with task
      }

      // save instance locally
      Instance.store(instanceObj, (progress) => {
        console.log('Saving instance progress:', progress);
        onProgress && onProgress(80 + 0.2 * progress);
      }, (instance) => {
        console.log('Instance saved:', instance);
        onProgress && onProgress(100);
        onDone && onDone(instance); // <--- when upgrading for multiple files, we need to increase process partially and wait untill all files are stored.
      });

    }); // onUnzip, onProgress, onFileProcessed

  } catch (error) {
    console.error('Error downloading into DICOM store:', error);
  }
}

// --- Main component ---
export default function TasksPage({ tasks }) {

  const form = useForm({});
  const [showDetails, setShowDetails] = useState({type: 'none', message: null});
  const [downloadProgress, setDownloadProgress] = useState({});
  const [localTasks, setLocalTasks] = useState(null);

  const query = new URLSearchParams(window.location.search);  // Access query parameters
  const highlightSID = query.get('SeriesInstanceUID');        // Get the specific ID from query parameters
  const highlightID = query.get('taskId');                    // Get the specific task ID from query parameters

  // Memorize options in localStorage 
  // TODO: could we outsource this into the table component?
  const [isDownloadIntoDicomStore, setIsDownloadIntoDicomStore] = useState(() => {
    try {
      return localStorage.getItem('isDownloadIntoDicomStore') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('isDownloadIntoDicomStore', isDownloadIntoDicomStore);
    } catch {}
  }, [isDownloadIntoDicomStore]);

  const [isHideCancelledAndExpiredTasks, setIsHideCancelledAndExpiredTasks] = useState(() => {
    try {
      return localStorage.getItem('isHideCancelledAndExpiredTasks') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('isHideCancelledAndExpiredTasks', isHideCancelledAndExpiredTasks);
    } catch {}
  }, [isHideCancelledAndExpiredTasks]);

  useEffect(() => {
    getAllInstances().then((instances) => {
      setLocalTasks(instances.map(instance => instance.taskId).filter(Boolean));
    }).catch((error) => {
      console.error('Error fetching local instances:', error);
    });
  }, []);

  // callbacks / functions
  const onTaskCancel = async (task) => {
    form.put(`/tasks/${task.id}/cancel`, {
      onSuccess: () => {
        console.log('Task cancelled successfully:', task.id);
      },
      onError: (error) => {
        console.error('Error cancelling task:', error);
      },
    });
  }

  // actions
  const TaskActions = (_, task) => (
    <div className="flex items-center gap-2">
      { task.status === 'created' && (
          <button
            onClick={e => {
              e.preventDefault();
              onTaskCancel(task);
            }}
            className="text-sm font-medium text-red-600 hover:text-red-500"
          >
            Cancel
          </button>
      )}
      { task.status === 'completed' && (
        <>
          { (!isDownloadIntoDicomStore || localTasks !== null && !localTasks.includes(task.id)) &&
            <div className="flex flex-col items-start">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (isDownloadIntoDicomStore) {
                    handleDownloadIntoDicomStore({task, onProgress: (progress) => {
                      setDownloadProgress(prev => ({ ...prev, [task.id]: progress }));
                    }});
                  } else {
                    handleDownload(task);
                  }
                }}
              >
                {downloadProgress[task.id] && (downloadProgress[task.id] == 100) ?
                  <span className='text-green-500'>Downloaded</span> :  
                  <span className='text-sky-600 hover:underline'>Download</span>
                }
              </button>
              {downloadProgress[task.id] && (downloadProgress[task.id] < 100) && (
                <div className="w-full bg-gray-200 rounded-full h-1 -mb-1">
                  <div
                    className="bg-blue-500 h-1 rounded-full"
                    style={{
                      width: `${downloadProgress[task.id]}%`,
                    }}
                  ></div>
                </div>
              )}
            </div>
          }
          <button
            onClick={e => {
              e.preventDefault();
              setShowDetails({type: 'log', message: task.metadata?.message || 'No log available.'});
            }}
            className="text-sm font-medium text-sky-600 hover:text-sky-500"
          >
            View Details
          </button>
        </>
      )}
      { task.status === 'failed' && (
        <button
          onClick={e => {
            e.preventDefault();
            setShowDetails({type: 'error', message: task.metadata?.error || 'No error message.'});
          }}
          className="text-sm font-medium text-red-600 hover:text-red-500"
        >
          View Error
        </button>
      )}
    </div>
  );

  const LinkedSeriesInstanceUID = (SeriesInstanceUID) => (
    <Link href={`/dicomstore?SeriesInstanceUID=${SeriesInstanceUID}`} className="text-sky-600 hover:text-sky-500">
      {SeriesInstanceUID}
    </Link>
  );

  // Define columns for the table
  const columns = [
    { key: 'uuid', label: 'Task ID', sortable: false, hideable: true, defaultVisible: false, width: 'w-1/6', display: shortenUUID },
    { key: 'SeriesInstanceUID', label: 'Series Instance UID', sortable: true, hideable: true, width: 'w-1/6', display: LinkedSeriesInstanceUID },
    { key: 'model', label: 'Model Name', sortable: true, width: 'w-1/6', repr: (model) => model.label },
    { key: 'status', label: 'Status', sortable: true, filterable: true, width: 'w-1/6', display: (status) => <TaskStatusBadge status={status} /> },
    { key: 'duration', label: 'Duration', sortable: true, hideable: true, width: 'w-1/6', repr: (_, task) => task.metadata?.duration, display: (value) => formatDuration(value) },
    { key: 'created_at', label: 'Creation Date', sortable: true, hideable: true, width: 'w-1/6', display: (value) => formattedDate(value) },
    { key: 'downloaded_at', label: 'Downloaded At', sortable: true, hideable: true, width: 'w-1/6' },
    { key: 'actions', label: 'Actions', sortable: false, width: 'w-1/12', display: TaskActions },
  ];

  return (
    <div>
      <Table
        name="tasks"
        columns={columns}
        rows={tasks.filter(task => {
          if (isHideCancelledAndExpiredTasks) {
            return task.status !== 'cancelled' && task.status !== 'expired';
          }
          return true;
        })}
        options={[
          { label: 'Download into Dicom Store', checked: isDownloadIntoDicomStore, onClick: () => setIsDownloadIntoDicomStore(!isDownloadIntoDicomStore) },
          { label: 'Hide cancelled and expired tasks', checked: isHideCancelledAndExpiredTasks, onClick: () => setIsHideCancelledAndExpiredTasks(!isHideCancelledAndExpiredTasks) },
        ]}
        highlight={ (row) => row.SeriesInstanceUID === highlightSID || row.id == highlightID }
        highlightClassName='bg-yellow-50'
      />

        {/* Log Popup */}
        {showDetails.message && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-[60%] w-full">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {showDetails.type === 'log' ? 'Task Log' : 'Task Error'}
              </h2>
              <pre className="text-sm text-gray-700 bg-gray-100 p-4 rounded overflow-auto max-h-64">
                {showDetails.message || 'No log available.'}
              </pre>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowDetails({type: 'none', message: null})}
                  className="text-sm font-medium text-sky-600 hover:text-sky-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  ); 
}